import React, { useEffect, useState } from 'react';
import { Coins, Rocket, Globe2, Sparkles, TrendingUp } from 'lucide-react';
import { API } from '../lib/api';
import { shortNum, shortWei } from '../lib/format';
import axios from 'axios';

const StatsWidget = () => {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: s }, revResp] = await Promise.allSettled([
          axios.get(`${API}/stats`),
          axios.get(`${API}/stats/revenue`),
        ]).then((results) => [
          results[0].status === 'fulfilled' ? results[0].value : { data: null },
          results[1].status === 'fulfilled' ? results[1].value : null,
        ]);
        if (!cancelled) {
          setStats(s);
          if (revResp) setRevenue(revResp.data);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !stats) return null;

  const format = (n) => Number(n || 0).toLocaleString();

  const cards = [
    { key: 'total', label: 'Coins forged', value: format(stats.total_tokens), icon: <Rocket />, tone: 'cyan' },
    { key: 'week', label: 'This week', value: format(stats.tokens_this_week), icon: <Sparkles />, tone: 'violet' },
    { key: 'chains', label: 'Networks', value: (stats.chains || []).length || 1, icon: <Globe2 />, tone: 'pink' },
    { key: 'supply', label: 'Supply forged', value: shortNum(stats.total_supply), icon: <Coins />, tone: 'cyan' },
  ];

  const tones = {
    cyan: 'from-[#7DF9FF]/15 to-[#7DF9FF]/[0.03] border-[#7DF9FF]/25 text-[#7DF9FF]',
    violet: 'from-[#A78BFA]/15 to-[#A78BFA]/[0.03] border-[#A78BFA]/25 text-[#A78BFA]',
    pink: 'from-[#F472B6]/15 to-[#F472B6]/[0.03] border-[#F472B6]/25 text-[#F472B6]',
  };

  return (
    <div data-testid="stats-widget" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-8">
      {cards.map((c) => (
        <div
          key={c.key}
          data-testid={`stat-${c.key}`}
          className={`rounded-2xl bg-gradient-to-br ${tones[c.tone]} border p-4 flex items-center gap-3`}
        >
          <div className={`shrink-0 w-10 h-10 rounded-xl bg-white/[0.06] flex items-center justify-center`}>
            {React.cloneElement(c.icon, { className: 'w-5 h-5' })}
          </div>
          <div className="min-w-0">
            <p className="text-[11px] text-forge-muted uppercase tracking-wider font-medium truncate">{c.label}</p>
            <p className="font-display text-2xl font-bold text-white truncate">{c.value}</p>
          </div>
        </div>
      ))}
      <div className="col-span-2 lg:col-span-4 rounded-2xl bg-gradient-to-r from-[#7DF9FF]/10 via-[#A78BFA]/10 to-[#F472B6]/10 border border-white/10 p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-forge-muted uppercase tracking-wider font-medium flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" /> Platform revenue · realized
          </p>
          {revenue ? (
            <>
              <p className="font-display text-2xl sm:text-3xl font-bold text-white mt-1" data-testid="realized-total">
                {shortWei(revenue.total_realized_wei)} <span className="text-forge-muted text-sm font-normal">tokens accrued</span>
              </p>
              <p className="text-xs text-forge-muted mt-1">
                <span data-testid="coins-earning" className="text-white font-medium">{revenue.coins_earning}</span> / {revenue.coins_checked} coins have already routed fees to the platform wallet.
                {revenue.by_chain?.length > 0 && (
                  <span className="ml-1 text-forge-muted">Top chain: <span className="text-white">{revenue.by_chain[0].chain_name}</span>.</span>
                )}
              </p>
            </>
          ) : (
            <p className="font-display text-lg text-forge-muted mt-1">Loading on-chain data…</p>
          )}
        </div>
        <div className="text-xs text-forge-muted max-w-xs shrink-0">
          <p className="text-[11px] uppercase tracking-wider text-forge-muted font-medium">Projected max</p>
          <p className="font-display text-lg text-white font-bold">{shortNum(stats.platform_tax_projected)} tokens</p>
          <p className="mt-0.5">1.0% of total supply forged — the ceiling if every coin fully circulates once.</p>
        </div>
      </div>
    </div>
  );
};

export default StatsWidget;
